import { ChangeEvent, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Icon } from "../Icon"

interface CustomComponentProps {
  onClick: () => void
  title: string
}

export interface FileUploadProps {
  onUpload: (file: File) => void
  onRemove: () => void
  customComponent?: ({ onClick, title }: CustomComponentProps) => void
  title: string
  accept?: string
  isLoading?: boolean
  file?: File
  setFile: (file: File | undefined) => void
}

export function FileUpload({
  onUpload,
  onRemove,
  title,
  accept,
  customComponent,
  isLoading,
  file,
  setFile,
}: FileUploadProps) {
  const fileUploadRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()

  useEffect(() => {
    const dropEvents = ["dragenter", "dragover", "dragleave", "drop"]
    let dropArea: HTMLElement | null = document.getElementById("file-upload")

    const preventDefaults = (e: any) => {
      e.preventDefault()
      e.stopPropagation()
    }

    dropEvents.forEach((e: string) => {
      dropArea?.addEventListener(e, preventDefaults, false)
    })
    ;["dragenter", "dragover"].forEach((e: string) => {
      dropArea?.addEventListener(e, highlight, false)
    })
    ;["dragleave", "drop"].forEach((e: string) => {
      dropArea?.addEventListener(e, unhighlight, false)
    })

    function highlight() {
      dropArea?.classList.add("hover:bg-[color:var(--alias-state-base-hover)]")
    }

    function unhighlight() {
      dropArea?.classList.remove(
        "hover:bg-[color:var(--alias-state-base-hover)]",
      )
    }

    dropArea?.addEventListener(
      "drop",
      (e: DragEvent) => {
        const file = e.dataTransfer?.files[0]
        if (file) {
          onUpload(file)
          setFile(file)
        }
      },
      false,
    )
  }, [onUpload, setFile])

  const removeFile = () => {
    onRemove()
    setFile(undefined)
  }

  const handleUploadClick = () => {
    fileUploadRef.current?.click()
  }

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      const file = e.target.files[0]
      onUpload(file)
      setFile(file)
      e.target.value = ""
    } else {
      throw new Error("Error uploading file")
    }
  }

  const renderState = () => {
    if (isLoading) {
      return (
        <button
          aria-label={title}
          title={title}
          disabled={true}
          className="flex flex-col justify-center items-center"
        >
          <span className="m-2 text-[color:var(--alias-color-text-link-default)] [font-family:var(--alias-font-subtitle-lg-font-weight)]">
            {t("client.fileupload.validating")}
          </span>
        </button>
      )
    }
    if (!file) {
      return (
        <button
          aria-label={title}
          title={title}
          onClick={handleUploadClick}
          className="flex flex-col justify-center items-center"
        >
          <Icon className="mr-2" variant="Upload" />
          <span className="m-2 text-[color:var(--alias-color-text-link-default)] [font-family:var(--alias-font-subtitle-lg-font-weight)]">
            {title}
          </span>
          <span className="text-[color:var(--alias-color-text-weaker)]">
            {accept} {t("client.fileupload.file")}
          </span>
        </button>
      )
    }
    return (
      <div className="flex flex-col justify-center items-center">
        <Icon className="mr-2" variant="Upload" />
        <span className="m-2 text-[color:var(--alias-color-text-weaker)]">
          {file.name}
        </span>
        <button
          className="text-[color:var(--alias-color-text-link-default)] [font-family:var(--alias-font-subtitle-lg-font-weight)]"
          onClick={removeFile}
        >
          {t("client.fileupload.deletefile")}
        </button>
      </div>
    )
  }

  return (
    <>
      {customComponent ? (
        customComponent({ onClick: handleUploadClick, title })
      ) : (
        <div
          className={`flex items-center justify-center w-full rounded-lg border-dashed border border-[color:var(--alias-color-border-hover)] h-28 ${
            !file && "hover:bg-[color:var(--alias-state-base-hover)]"
          }`}
          id="file-upload"
        >
          {renderState()}
        </div>
      )}
      <input
        data-testid="file_upload"
        type="file"
        ref={fileUploadRef}
        onChange={handleFileUpload}
        style={{ display: "none" }}
        accept={accept}
      />
    </>
  )
}
